/**
 * @fileoverview Hook to detect user's reduced motion preference
 *
 * Respects the user's `prefers-reduced-motion` media query setting.
 * Used to disable or reduce animations for users with vestibular disorders
 * or those who prefer minimal motion for performance/battery life.
 *
 * @module client/hooks/use-reduced-motion
 */

import { useEffect, useState } from 'react';

/**
 * Hook to detect if user prefers reduced motion.
 *
 * Listens to the `prefers-reduced-motion` media query and updates
 * when the preference changes (e.g., user toggles system setting).
 *
 * @returns {boolean} True if user prefers reduced motion, false otherwise
 *
 * @example
 * ```tsx
 * function AnimatedComponent() {
 *   const prefersReducedMotion = useReducedMotion();
 *
 *   return (
 *     <motion.div
 *       initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
 *       animate={{ opacity: 1, y: 0 }}
 *       transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
 *     >
 *       Content
 *     </motion.div>
 *   );
 * }
 * ```
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-reduced-motion
 * @see https://www.w3.org/WAI/WCAG21/Understanding/animation-from-interactions.html
 */
export function useReducedMotion(): boolean {
  // Default to false (animations enabled) during SSR or when matchMedia is unavailable
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if matchMedia is available (not available during SSR)
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    // Create media query
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Handler for when preference changes
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setPrefersReducedMotion(event.matches);
    };

    // Listen for changes (use addEventListener for modern browsers, addListener for legacy)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Legacy Safari support
      mediaQuery.addListener(handleChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Legacy Safari support
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return prefersReducedMotion;
}
